import cn from 'classnames';
import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import { useSetError } from '@src/redux/modals';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Design, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { AdvertiserMatch, confirmOnboard, OnboardSearchResult, searchOnboard } from './auditApi';

// Only pre-select the top match when we're confident. A low-confidence auto-pick silently links the
// wrong advertiser, whose data then feeds the audit — so below this the user must choose deliberately.
const AUTO_SELECT_MIN_SCORE = 0.8;

interface ClientOnboardingProps {
  onConfirm: (cid: string) => void;
  onClose: () => void;
  // Seeded when the user arrives from a "needs setup" row in the client picker, so they don't have
  // to retype the client they just clicked.
  initialQuery?: string;
}

export const ClientOnboarding = ({
  onConfirm,
  onClose,
  initialQuery = ''
}: ClientOnboardingProps) => {
  const [query, setQuery] = useState(initialQuery);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<OnboardSearchResult | null>(null);
  const [selectedBpm, setSelectedBpm] = useState<AdvertiserMatch | null>(null);
  const [selectedNexxen, setSelectedNexxen] = useState<AdvertiserMatch | null>(null);
  const [selectedFt, setSelectedFt] = useState<AdvertiserMatch | null>(null);
  // F9d — whether this client runs linear TV. Drives linear-specific rule applicability; set at
  // onboarding rather than assumed off, so linear questions aren't silently N/A'd for a linear client.
  const [runsLinear, setRunsLinear] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const showError = useSetError();

  const onSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setResults(null);
    setSelectedBpm(null);
    setSelectedNexxen(null);
    setSelectedFt(null);
    setError('');
    try {
      const r = await searchOnboard(query.trim());
      setResults(r);
      if (r.bpm[0] && r.bpm[0].score >= AUTO_SELECT_MIN_SCORE) {
        setSelectedBpm(r.bpm[0]);
      }
      if (r.nexxen[0] && r.nexxen[0].score >= AUTO_SELECT_MIN_SCORE) {
        setSelectedNexxen(r.nexxen[0]);
      }
      if (r.ft[0] && r.ft[0].score >= AUTO_SELECT_MIN_SCORE) {
        setSelectedFt(r.ft[0]);
      }
    } catch (e) {
      setError(e.message);
      showError({ message: `Advertiser search failed: ${e.message}`, reportError: e });
    } finally {
      setSearching(false);
    }
  };

  const onConfirmClick = async () => {
    if (!selectedBpm) return;
    setSaving(true);
    setError('');
    try {
      const { cid } = await confirmOnboard(
        selectedBpm.id,
        selectedBpm.name,
        runsLinear,
        selectedNexxen?.id,
        selectedFt?.id
      );
      onConfirm(cid);
    } catch (e) {
      setError(e.message);
      showError({ message: `Couldn't onboard this client: ${e.message}`, reportError: e });
      setSaving(false);
    }
  };

  return (
    <InteractiveDiv className='client-picker-overlay' onClick={onClose}>
      <InteractiveDiv className='client-onboarding' onClick={e => e.stopPropagation()}>
        <div className='onboarding-header'>
          <span className='onboarding-title'>Add client</span>
          <IconButton appearance={Appearance.EMPTY} design={Design.NEUTRAL} onClick={onClose}>
            <MdClose />
          </IconButton>
        </div>

        <div className='onboarding-search'>
          <Form.Control
            autoFocus
            placeholder='Client name (e.g. "Mistplay")'
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSearch()}
          />
          <Button size={Size.LG} onClick={onSearch} disabled={!query.trim()} loading={searching}>
            Search
          </Button>
        </div>

        {error && <div className='onboarding-error'>{error}</div>}

        {results && (
          <div className='onboarding-results'>
            <MatchSection
              label='BPM client'
              badge='Required'
              matches={results.bpm}
              selected={selectedBpm}
              onSelect={setSelectedBpm}
              allowNone={false}
              emptyMsg='No BPM match — try a different spelling'
            />
            <MatchSection
              label='Nexxen advertiser'
              badge='DSP rows'
              matches={results.nexxen}
              selected={selectedNexxen}
              onSelect={setSelectedNexxen}
              allowNone
              emptyMsg='Not found in Nexxen'
              error={results.nexxenError}
            />
            <MatchSection
              label='Flashtalking advertiser'
              badge='FT rows'
              matches={results.ft}
              selected={selectedFt}
              onSelect={setSelectedFt}
              allowNone
              emptyMsg='Not found in Flashtalking'
              error={results.ftError}
            />

            <Checkbox
              className='onboarding-linear'
              label='This client runs linear TV'
              title='Enables linear-specific rules for this client; leave off for streaming-only clients'
              checked={runsLinear}
              onChange={e => setRunsLinear(e.target.checked)}
            />

            <div className='onboarding-footer'>
              {selectedBpm ? (
                <span className='onboarding-summary'>
                  Will link {selectedBpm.name}
                  {selectedNexxen
                    ? ` · Nexxen #${selectedNexxen.id}`
                    : ' · no Nexxen (DSP rows stay manual)'}
                  {selectedFt
                    ? ` · Flashtalking #${selectedFt.id}`
                    : ' · no Flashtalking (FT rows stay manual)'}
                </span>
              ) : (
                <span className='onboarding-warning'>Select a BPM client to continue</span>
              )}
              <Button
                size={Size.LG}
                disabled={!selectedBpm}
                loading={saving}
                onClick={onConfirmClick}
              >
                Set up client
              </Button>
            </div>
          </div>
        )}
      </InteractiveDiv>
    </InteractiveDiv>
  );
};

interface MatchSectionProps {
  label: string;
  badge: string;
  matches: AdvertiserMatch[];
  selected: AdvertiserMatch | null;
  onSelect: (m: AdvertiserMatch | null) => void;
  allowNone: boolean;
  emptyMsg: string;
  // Set when the connector SEARCH failed (outage) rather than returned no matches — shown instead of
  // emptyMsg so ops don't mistake an outage for "not found" and onboard the client connector-less.
  error?: string;
}

const MatchSection = ({
  label,
  badge,
  matches,
  selected,
  onSelect,
  allowNone,
  emptyMsg,
  error
}: MatchSectionProps) => (
  <div className='match-section'>
    <div className='match-section-header'>
      <span className='match-section-label'>{label}</span>
      <span className='match-section-badge'>{badge}</span>
    </div>
    {error && <div className='match-error'>{error} — search unavailable; try again.</div>}
    {!error && matches.length === 0 && <div className='match-empty'>{emptyMsg}</div>}
    {!error && matches.length > 0 && (
      <div className='match-pills'>
        {matches.map(m => (
          <button
            key={m.id}
            type='button'
            className={cn('match-pill', { 'is-selected': selected?.id === m.id })}
            onClick={() => onSelect(m)}
          >
            {m.name}
            <span className='match-pill-id'>#{m.id}</span>
            <span className='match-pill-score' title='Match confidence'>
              {Math.round(m.score * 100)}%
            </span>
          </button>
        ))}
        {allowNone && (
          <button
            type='button'
            className={cn('match-pill match-pill-skip', { 'is-selected': selected === null })}
            onClick={() => onSelect(null)}
          >
            Skip
          </button>
        )}
      </div>
    )}
  </div>
);
